/**
 * Demo Data API
 *
 * Wraps `/api/v1/demo-data/*` endpoints (seed/swap/status/clear).
 * Status response is validated against the api-types schema
 * (`demoDataStatusResponseSchema`) since UI flows depend on its shape.
 */

import {
  demoDataStatusResponseSchema,
  type DemoDataStatusResponse,
} from '@aica/api-types/schemas';
import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';

export type DemoStatusResponse = DemoDataStatusResponse;
export type DemoIndustry = NonNullable<DemoDataStatusResponse['industry']>;
export type DemoLocale = NonNullable<DemoDataStatusResponse['locale']>;

export interface DemoSeedResponse {
  success: boolean;
  recordCount: number;
  ahaCallId: string;
  industry: DemoIndustry;
  alreadySeeded: boolean;
}

export interface DemoSwapResponse {
  success: boolean;
  recordCount: number;
  ahaCallId: string;
  industry: DemoIndustry;
}

export interface DemoClearResponse {
  success: boolean;
  deleted: boolean;
}

export class DemoApi extends BaseApiClient {
  async getStatus(): Promise<DemoStatusResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.DEMO_DATA_STATUS,
      demoDataStatusResponseSchema
    );
  }

  async seed(
    industry: DemoIndustry = 'generic',
    locale: DemoLocale = 'en'
  ): Promise<DemoSeedResponse> {
    return this.request<DemoSeedResponse>(API_ENDPOINTS.DEMO_DATA_SEED, {
      method: 'POST',
      body: JSON.stringify({ industry, locale }),
    });
  }

  async swap(
    toIndustry: DemoIndustry,
    locale: DemoLocale = 'en'
  ): Promise<DemoSwapResponse> {
    return this.request<DemoSwapResponse>(API_ENDPOINTS.DEMO_DATA_SWAP, {
      method: 'POST',
      body: JSON.stringify({ industry: toIndustry, locale }),
    });
  }

  async clear(): Promise<DemoClearResponse> {
    return this.request<DemoClearResponse>(API_ENDPOINTS.DEMO_DATA_CLEAR, {
      method: 'DELETE',
    });
  }
}
